import * as Sentry from '@sentry/react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/modules/shared/store/appStore';
import { Suspense, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import AuthenticatedHeader from '../../components/authenticated-header';
import Header from '../../components/latest-header';
import MainNavigation from '../../components/main-navigation';
import {
  detectPlatform,
  getAccessToken,
  getParameterByName,
  getUserData,
  isAdmin,
  isAgent,
  isInspectionGarage,
  isMlmAgent,
  isSubAgent,
  isSuperAdmin,
  unregisterTracking,
  needsRedirection,
} from '../../utils/helper';
import LatestHeader from '../latest-header';
import { SidebarProvider } from '@/components/ui/sidebar';
import { PortalNavVisibleContext } from '@/modules/shared/hooks/usePortalNavVisible';
import { QueryProvider } from '@/providers/query-provider';
import { NotificationStreamProvider } from '@/modules/shared/notifications/NotificationStreamProvider';

/** Auth flow pages — never show app chrome (sidebar/header) on these */
const AUTH_FLOW_PATHS = ['/auth/', '/otp', '/passcode-verification', '/create-passcode', '/welcome'];

function isAuthFlowPath(pathname: string) {
  return AUTH_FLOW_PATHS.some((p) => pathname === p || pathname.startsWith(p));
}

// `/chatwoot-payments` renders inside Chatwoot's own panel, which supplies the surrounding
// chrome — the portal sidebar/header would be a second navigation inside someone else's UI.
const IGNORE_SIDEBAR = [
  '/',
  '/chatwoot-payments',
  '/personal-accident',
  '/corona-wizard',
  '/dengue-wizard',
  '/pa-wizard',
];

const IGNORE_HEADER = ['/chatwoot-payments', '/downline'];

// `/ops-dashboard?conversation_id=…` is the Chatwoot conversation view. It brings its own top
// chrome — brand, quotation search, refresh, language, theme, account — in the quotation tab bar,
// and is framed beside a conversation, so the portal's navigation menu is a second left rail and
// its breadcrumb header a second title bar, neither of which the operator has any use for there.
// Suppresses BOTH (see `shouldShowSidebar` and `CustomHeader`). Plain `/ops-dashboard` keeps them.
function isChatwootConversation(pathname: string) {
  if (pathname !== '/ops-dashboard') return false;
  // `"0"` is the sentinel Chatwoot puts on the URL outside a conversation — same rule as
  // `getConversationIdParam()` / `resolveMode()` in the ops-dashboard module.
  const id = getParameterByName('conversation_id');
  const value = id != null ? String(id).trim() : '';
  return value !== '' && value !== '0';
}

/**
 * Remembers whether this browser has ever been shown the sidebar.
 *
 * `shouldShowSidebar` cannot be answered at first paint: it needs `affiliate`, and the store
 * persists only `language`, so the affiliate arrives with the bootstrap fetch a beat later.
 * Until then the content pane renders full width; when the sidebar appears the pane jumps one
 * `--sidebar-width` to the right. Lighthouse measured that single shift at 0.1796 of a 0.18
 * CLS, identically on /create-leads and /ops-dashboard (2026-09-19) — effectively the whole
 * layout-shift penalty on both pages.
 *
 * So reserve the space up front for anyone who had it last time. Self-correcting: a user who
 * never gets a sidebar never writes the flag, and so never sees a reserved gutter.
 */
const SIDEBAR_SEEN_KEY = 'sidebar_seen';

function readSidebarSeen(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_SEEN_KEY) === '1';
  } catch {
    // Private mode or blocked storage: reserve nothing, i.e. exactly today's behaviour.
    return false;
  }
}

function writeSidebarSeen(seen: boolean) {
  try {
    localStorage.setItem(SIDEBAR_SEEN_KEY, seen ? '1' : '0');
  } catch {
    // Ignore. The reservation is an optimisation, never a correctness requirement.
  }
}

type Affiliate = Record<string, unknown> | null;

interface LayoutProps {
  children?: ReactNode;
  className?: string;
  containerClassName?: string;
}

export function Layout({ children, className }: LayoutProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();

  // Global app state (Zustand)
  const affiliate = useAppStore((state) => state.affiliate);

  // URL parameters
  const isIg = getParameterByName('ig') === 'true';
  const isDirectAgent = getParameterByName('direct_agent') === 'true';
  const isBusinessManager = getParameterByName('business_manager') === 'true';

  // Replace componentDidCatch with error boundary or effect
  useEffect(() => {
    const handleError = (error: unknown, errorInfo?: Record<string, unknown>) => {
      const userData = getUserData();
      let extra: Record<string, unknown> = {};
      if (userData) {
        extra.data = userData;
      }
      extra = { ...extra, ...errorInfo };
      // Was `window.Raven.captureException`. Raven is Sentry's 2018 SDK and only ever loaded
      // on fairdee.co.th, so on admin.fairdee.co.th `window.Raven` was undefined and every
      // error this handler caught went nowhere. @sentry/react is initialised in App.tsx with
      // the DSN from runtime config, on every host.
      Sentry.captureException(error, { extra });
    };

    // Set up error handling if needed
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Replace componentWillReceiveProps
  useEffect(() => {
    if (affiliate && needsRedirection(affiliate)) {
      unregisterTracking();
    }
  }, [pathname, affiliate]);

  // Determine if user is logged in
  const isLoggedIn = !!getAccessToken();

  // Platform detection
  const platform = detectPlatform();

  // Everything the sidebar's visibility turns on that is knowable before the affiliate
  // lands: the token and the route. Both are synchronous on the very first render.
  // `isChatwootConversation` joins the other two route tests rather than sitting with the
  // affiliate-dependent clauses below: it is knowable on the first render, so the conversation view
  // must not reserve the sidebar's width either (see `reserveSidebarSpace`).
  const sidebarPossibleHere =
    isLoggedIn && !IGNORE_SIDEBAR.includes(pathname) && !isAuthFlowPath(pathname) && !isChatwootConversation(pathname);

  // Check if sidebar should be shown
  const shouldShowSidebar = Boolean(
    sidebarPossibleHere &&
    affiliate &&
    affiliate.id &&
    ((!isAgent() && !isMlmAgent()) || isInspectionGarage() || isSubAgent() || isAdmin()),
  );

  // Hold the sidebar's width while the affiliate is still in flight, so the content pane does
  // not move sideways when it lands. See SIDEBAR_SEEN_KEY.
  // Read once per mount, not per render: this component re-renders on every navigation and
  // localStorage access is synchronous.
  const [sidebarSeen] = useState(readSidebarSeen);
  const reserveSidebarSpace = sidebarPossibleHere && !shouldShowSidebar && sidebarSeen;

  // FTT-16592: the ops dashboard opens with the sidebar collapsed (Step 2 Compact), every other page
  // expanded. Not remembered — moving between the two re-applies the page's default.
  const isOpsPage = pathname === '/ops-dashboard';
  const [sidebarOpen, setSidebarOpen] = useState(!isOpsPage);
  useLayoutEffect(() => setSidebarOpen(!isOpsPage), [isOpsPage]);

  useEffect(() => {
    // Only record an answer once the affiliate is actually known; before that
    // `shouldShowSidebar` is false for want of data, not because this user has no sidebar.
    if (!sidebarPossibleHere || !affiliate) return;
    writeSidebarSeen(shouldShowSidebar);
  }, [sidebarPossibleHere, affiliate, shouldShowSidebar]);

  return (
    <QueryProvider>
      {/* Above the sidebar and the router both, so navigating to a path on IGNORE_SIDEBAR
          no longer takes the SSE connection down with the sidebar. */}
      <NotificationStreamProvider enabled={isLoggedIn && !isAuthFlowPath(pathname)}>
        <SidebarProvider
          open={sidebarOpen}
          onOpenChange={setSidebarOpen}
          className={cn((shouldShowSidebar || reserveSidebarSpace) && 'flex')}
        >
          <PortalNavVisibleContext.Provider value={shouldShowSidebar}>
            <div className={cn('min-h-screen w-full bg-background font-sans antialiased flex', className)}>
              {shouldShowSidebar && <MainNavigation />}
              {reserveSidebarSpace && (
                <div
                  aria-hidden
                  className={cn(
                    'hidden shrink-0 nav:block',
                    isOpsPage ? 'w-(--sidebar-width-icon)' : 'w-(--sidebar-width)',
                  )}
                />
              )}
              <div className="w-[300px] flex-1">
                <div className="bg-card">
                  <CustomHeader
                    isLoggedIn={isLoggedIn}
                    affiliate={affiliate}
                    isIg={isIg}
                    isDirectAgent={isDirectAgent}
                    isBusinessManager={isBusinessManager}
                    pathname={pathname}
                  />
                </div>
                <main>
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center min-h-[200px]">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    }
                  >
                    {children}
                  </Suspense>
                </main>
              </div>
            </div>
          </PortalNavVisibleContext.Provider>
        </SidebarProvider>
      </NotificationStreamProvider>
    </QueryProvider>
  );
}

interface CustomHeaderProps {
  isLoggedIn: boolean;
  affiliate: Affiliate;
  isIg: boolean;
  isDirectAgent: boolean;
  isBusinessManager: boolean;
  pathname: string;
}

const CustomHeader = ({
  isLoggedIn,
  affiliate,
  isIg,
  isDirectAgent,
  isBusinessManager,
  pathname,
}: CustomHeaderProps) => {
  // Auth flow pages never show app chrome — render nothing
  if (isAuthFlowPath(pathname)) {
    return <></>;
  }

  if (isLoggedIn && affiliate && affiliate.id) {
    if (IGNORE_HEADER.includes(pathname) || isChatwootConversation(pathname)) {
      return <></>;
    }
    if (!isAdmin() && !isSuperAdmin() && (isAgent() || isMlmAgent())) {
      return <Header />;
    }
    return <AuthenticatedHeader />;
  }
  if (!isLoggedIn) {
    return <LatestHeader isIg={isIg} isDirectAgent={isDirectAgent} isBusinessManager={isBusinessManager} />;
  }
  return <></>;
};
